/**
 * MobileGuards.tsx — Top-level mobile UX overlays
 *
 * BETA 16.66 — Per Dylan: "i would love if it forced landscape mode.
 * right now you can play verticle. doesnt look that good verticle."
 *
 * Two overlays that need to be rendered at the OUTERMOST app level
 * (above start menu, settings, game, leaderboard, all of it) so the
 * user can never sneak past them:
 *
 *   1. PortraitGuard — full-black "ROTATE TO LANDSCAPE" screen shown
 *      whenever a touch device is in portrait. Blocks ALL interaction
 *      until the user rotates. Auto-disappears on landscape orientation.
 *
 *   2. AddToHomeScreenHint — one-shot banner on iPhone Safari that
 *      explains how to install the PWA for true fullscreen. Dismissed
 *      via localStorage.
 *
 * These were previously rendered inside TouchControls (in-game only).
 * Moved here so they cover the start menu / settings / leaderboard too —
 * Dylan playing in portrait on the menu was unblocked, not just in-game.
 *
 * Also fires the auto-fullscreen + orientation lock attempts on first
 * touch at the Home level — works on Android Chrome + iPad. iPhone
 * Safari rejects both (Apple), so the PWA path is the answer there.
 */
import { useEffect, useState } from 'react';
import { isTouchDevice, isIPhoneSafari, isStandalonePWA } from './TouchControls';
// BETA 16.71 — Handheld detection (Steam Deck / ROG Ally / Switch) so
// we can show an install hint on Windows handhelds that mirrors the
// iPhone Add-to-Home-Screen prompt.
import { classifyDevice, isHandheldGamingDevice } from '../game/deviceDetect';
// BETA 16.76 — Global gamepad poller for menu navigation (runs even
// when the game isn't active so the home / settings / pause screens
// can receive controller input).
import { installMenuGamepadTicker } from '../game/gamepadInput';

export default function MobileGuards() {
  const [show, setShow] = useState<boolean>(() => isTouchDevice());
  const [isPortrait, setIsPortrait] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerHeight > window.innerWidth,
  );
  const [showAddHomeHint, setShowAddHomeHint] = useState<boolean>(false);

  useEffect(() => {
    setShow(isTouchDevice());
    // BETA 16.76 — Mount the global gamepad menu ticker exactly once.
    // Idempotent (the function is internally guarded). Lets controller
    // navigate every menu screen even before a run starts.
    installMenuGamepadTicker();
  }, []);

  useEffect(() => {
    const checkOrient = () => setIsPortrait(window.innerHeight > window.innerWidth);
    window.addEventListener('resize', checkOrient);
    window.addEventListener('orientationchange', checkOrient);
    checkOrient();
    return () => {
      window.removeEventListener('resize', checkOrient);
      window.removeEventListener('orientationchange', checkOrient);
    };
  }, []);

  useEffect(() => {
    // Add to Home Screen / Install hint:
    //   - iPhone Safari: tap Share → Add to Home Screen
    //   - Handheld (ROG Ally / Steam Deck): Edge/Chrome → ⋮ → Install this site
    //   - Desktop: handled by browser's native install button (no overlay)
    // Suppressed after first dismiss via localStorage (per device).
    if (isStandalonePWA()) return;
    if (isIPhoneSafari() || isHandheldGamingDevice()) {
      try {
        if (localStorage.getItem('goopzone_a2hs_dismissed') !== '1') {
          setShowAddHomeHint(true);
        }
      } catch { /* */ }
    }
  }, []);

  // Auto-fullscreen + orientation lock on first interaction.
  // BETA 16.75 — Per Dylan (ROG Ally X): "also there is a top bar
  // showing." Edge on Windows (including the ROG Ally X PWA install)
  // launches PWAs in `standalone` display mode but STILL shows the
  // browser's own title bar and window chrome. So previously skipping
  // the fullscreen attempt when isStandalonePWA() returned true left
  // Windows handhelds stuck with a title bar. Now: fire requestFullscreen
  // anyway on Windows / handheld devices when in standalone — Edge will
  // honor it and hide the title bar. iPhone PWAs already get true
  // fullscreen in standalone (Apple), so for those we still skip.
  useEffect(() => {
    if (!isTouchDevice() && !isHandheldGamingDevice()) return;
    // iPhone PWA already has real fullscreen; only skip there.
    const ua = navigator.userAgent ?? '';
    const isAppleStandalone = isStandalonePWA() && /iphone|ipad|ipod/i.test(ua);
    if (isAppleStandalone) return;
    let fired = false;
    const tryFullscreen = () => {
      if (fired) return;
      fired = true;
      try {
        const el = document.documentElement;
        void el.requestFullscreen?.().catch(() => { /* ignored — iPhone Safari */ });
        const so = (screen as unknown as { orientation?: { lock?: (o: string) => Promise<void> } }).orientation;
        void so?.lock?.('landscape').catch(() => { /* ignored */ });
      } catch { /* ignore */ }
      window.removeEventListener('touchstart', tryFullscreen);
      window.removeEventListener('pointerdown', tryFullscreen);
    };
    window.addEventListener('touchstart', tryFullscreen, { passive: true });
    window.addEventListener('pointerdown', tryFullscreen, { passive: true });
    return () => {
      window.removeEventListener('touchstart', tryFullscreen);
      window.removeEventListener('pointerdown', tryFullscreen);
    };
  }, []);

  const dismissAddHome = () => {
    setShowAddHomeHint(false);
    try { localStorage.setItem('goopzone_a2hs_dismissed', '1'); } catch { /* */ }
  };

  if (!show) return null;

  return (
    <>
      {/* PORTRAIT GUARD — covers everything (start menu, game, settings,
          leaderboard) when on a touch device in portrait. Highest z-index. */}
      {isPortrait && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.96)',
            color: '#fff',
            zIndex: 99999,
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            textAlign: 'center',
            fontFamily: "'Orbitron', 'Share Tech Mono', monospace",
          }}
        >
          <div style={{ fontSize: 64, marginBottom: 18 }}>🔄</div>
          <div style={{
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: '0.18em',
            marginBottom: 14,
            color: '#ffaa3c',
            textShadow: '0 0 12px rgba(255,170,60,0.55)',
          }}>
            ROTATE TO LANDSCAPE
          </div>
          <div style={{ fontSize: 14, opacity: 0.85, lineHeight: 1.55, maxWidth: 320, letterSpacing: '0.06em' }}>
            TheBurntPeanut Simulator is built for landscape. Turn your device sideways for the full UI.
          </div>
          {isIPhoneSafari() && !isStandalonePWA() && (
            <div style={{
              marginTop: 28,
              padding: '12px 16px',
              border: '1px solid rgba(74,158,255,0.55)',
              borderRadius: 6,
              background: 'rgba(74,158,255,0.10)',
              maxWidth: 340,
              fontSize: 12,
              lineHeight: 1.5,
              letterSpacing: '0.05em',
              color: '#cfe8ff',
            }}>
              <div style={{ color: '#74b8ff', fontWeight: 700, marginBottom: 4 }}>📱 BEST EXPERIENCE</div>
              Tap Share → <b>Add to Home Screen</b> in Safari.<br/>
              Open the icon for true fullscreen, locked landscape, no URL bar.
            </div>
          )}
        </div>
      )}

      {/* INSTALL HINT — iPhone Safari + Handheld gaming devices (ROG Ally,
          Steam Deck, Nintendo Switch). Different copy per platform.
          Suppressed under the portrait rotate overlay so it doesn't pile
          on top. One-shot, dismissible to localStorage. */}
      {showAddHomeHint && !isPortrait && (
        <div
          style={{
            position: 'fixed',
            top: 'max(12px, calc(env(safe-area-inset-top) + 8px))',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9990,
            pointerEvents: 'auto',
            background: 'rgba(0,0,0,0.94)',
            border: '2px solid rgba(74,158,255,0.85)',
            borderRadius: 8,
            padding: '12px 16px',
            color: '#fff',
            fontFamily: "'Share Tech Mono', monospace",
            fontSize: 13,
            maxWidth: 420,
            letterSpacing: '0.05em',
            boxShadow: '0 0 18px rgba(74,158,255,0.4)',
            textAlign: 'center',
          }}
        >
          {isIPhoneSafari() ? (
            <>
              <div style={{ fontWeight: 700, color: '#74b8ff', marginBottom: 6, letterSpacing: '0.12em' }}>
                📱 GET FULLSCREEN ON iPHONE
              </div>
              <div style={{ lineHeight: 1.45, opacity: 0.92 }}>
                Tap the <b>Share</b> button → <b>Add to Home Screen</b>. Open the icon for true fullscreen + locked landscape.
              </div>
            </>
          ) : classifyDevice() === 'rog-ally' ? (
            <>
              <div style={{ fontWeight: 700, color: '#74b8ff', marginBottom: 6, letterSpacing: '0.12em' }}>
                🎮 INSTALL ON ROG XBOX ALLY (NO WINDOWS MODE)
              </div>
              <div style={{ lineHeight: 1.45, opacity: 0.92, textAlign: 'left' }}>
                1. Already in Edge — look at the address bar, find the <b>App available</b> icon (a monitor with a down-arrow) and tap it. Tap <b>Install</b>.<br/>
                2. Back in <b>Xbox FSE → Library → My Apps</b>, tap the <b>+</b> top-right and add the new "Peanut Sim" app to your library.<br/>
                3. Launch it from the library — fullscreen, controller works automatically. No Windows desktop ever.
              </div>
            </>
          ) : classifyDevice() === 'steam-deck' ? (
            <>
              <div style={{ fontWeight: 700, color: '#74b8ff', marginBottom: 6, letterSpacing: '0.12em' }}>
                🎮 INSTALL ON STEAM DECK
              </div>
              <div style={{ lineHeight: 1.45, opacity: 0.92 }}>
                In the Chrome address bar: tap the <b>Install</b> icon (⊞) on the right side. Adds it to Steam as a non-Steam game so you can launch it from Big Picture.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 700, color: '#74b8ff', marginBottom: 6, letterSpacing: '0.12em' }}>
                🎮 INSTALL FOR FULLSCREEN
              </div>
              <div style={{ lineHeight: 1.45, opacity: 0.92 }}>
                In Chrome / Edge: open the menu and pick <b>Install this site</b>. The icon launches as a fullscreen app — better for handheld + controller play.
              </div>
            </>
          )}
          <button
            onClick={dismissAddHome}
            onTouchStart={(e) => { e.preventDefault(); dismissAddHome(); }}
            style={{
              marginTop: 10,
              padding: '6px 18px',
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.18em',
              color: '#fff',
              background: 'rgba(74,158,255,0.20)',
              border: '1px solid rgba(74,158,255,0.65)',
              borderRadius: 4,
              cursor: 'pointer',
              touchAction: 'none',
            }}
          >
            GOT IT
          </button>
        </div>
      )}
    </>
  );
}
